import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Section, Container, t, BtnWhite, BtnOutlineWhite } from '../styles/shared';
import { resolveMediaServeUrl } from '../utils/mediaServeUrls';
import {
  resolvePresentation,
  backgroundSizeForFit,
} from '../utils/blockPresentation';

const HeroWrap = styled(Section)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${({ $minHeight }) => $minHeight || '70vh'};
  background-size: ${({ $bgSize }) => $bgSize || 'cover'};
  background-position: ${({ $bgPosition }) => $bgPosition || 'center'};
  background-repeat: no-repeat;
  background-image: ${({ $bg }) => $bg ? `url(${$bg})` : 'none'};
  background-color: ${({ $bgColor }) => $bgColor || t.primary};
  text-align: ${({ $align }) => $align || 'center'};
  color: ${({ $textColor }) => $textColor || '#fff'};
`;

const Overlay = styled.div`
  position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 1;
  background-color: ${({ $opacity }) => `rgba(0,0,0,${$opacity})`};
`;

const Content = styled.div`
  position: relative; z-index: 2; width: 100%; padding: 60px 0;
`;

const Headline = styled.h1`
  margin: 0 0 16px 0;
  font-family: ${t.fontHeading};
  font-size: 3.5rem; font-weight: 700; line-height: 1.2;
  color: ${({ $color }) => $color || '#fff'};
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  @media (max-width: 1024px) { font-size: 2.75rem; }
  @media (max-width: 768px)  { font-size: 2.25rem; }
`;

const Subheadline = styled.p`
  margin: 0 0 32px 0; font-size: 1.375rem; opacity: 0.95; max-width: 600px;
  color: ${({ $color }) => $color || '#fff'};
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  ${({ $center }) => $center && 'margin-left: auto; margin-right: auto;'}
  @media (max-width: 768px) { font-size: 1.125rem; }
`;

const Buttons = styled.div`
  display: flex; gap: 16px; flex-wrap: wrap;
  ${({ $center }) => $center && 'justify-content: center;'}
  @media (max-width: 768px) { flex-direction: column; align-items: center; }
`;

const isExt = (url) => url?.startsWith('http://') || url?.startsWith('https://');

const HeroBlock = ({ content = {}, settings = {}, tenantSlug = '', apiBase = '/api/v1' }) => {
  const {
    headline = '', subheadline = '', backgroundImage = '',
    overlayOpacity = 0.4, alignment = 'center', minHeight = '70vh',
    primaryButton = {}, secondaryButton = {}
  } = content;
  const { backgroundColor = '', textColor = '' } = settings;
  const presentation = resolvePresentation(settings, {
    // Today's hardcoded Hero behavior — emitted only as fallbacks when unset.
    defaultFit: 'cover',
    defaultPosition: 'center',
    defaultHeight: minHeight || '70vh',
  });
  const center = alignment === 'center';
  const resolvedBg = resolveMediaServeUrl(backgroundImage, tenantSlug, apiBase);
  const resolvedTextColor = textColor || '#ffffff';
  const bgSize = backgroundSizeForFit(presentation.objectFit) || 'cover';

  const renderBtn = (btn, Comp) => {
    if (!btn?.text) return null;
    if (isExt(btn.link)) return <Comp as="a" href={btn.link} target="_blank" rel="noopener noreferrer" $size="lg">{btn.text}</Comp>;
    return <Comp as={Link} to={btn.link || '/'} $size="lg">{btn.text}</Comp>;
  };

  return (
    <HeroWrap
      $bg={resolvedBg}
      $bgColor={backgroundColor}
      $align={alignment}
      $minHeight={presentation.imageHeight}
      $bgSize={bgSize}
      $bgPosition={presentation.objectPosition || 'center'}
      $textColor={resolvedTextColor}
      $fullBleed={presentation.fullBleed}
    >
      {resolvedBg && <Overlay $opacity={overlayOpacity} />}
      <Content>
        <Container $fullBleed={presentation.fullBleed} style={{ textAlign: alignment }}>
          {headline && <Headline $color={resolvedTextColor}>{headline}</Headline>}
          {subheadline && <Subheadline $color={resolvedTextColor} $center={center}>{subheadline}</Subheadline>}
          {(primaryButton?.text || secondaryButton?.text) && (
            <Buttons $center={center}>
              {renderBtn(primaryButton, BtnWhite)}
              {renderBtn(secondaryButton, BtnOutlineWhite)}
            </Buttons>
          )}
        </Container>
      </Content>
    </HeroWrap>
  );
};

export default HeroBlock;
